import {
    MediaPack,
    MediaPackProgram,
    RbcCircleRec,
} from '@calm/evexia-web-sdk';
import { RelatedIdWorkflowMap } from '@rhg/hooks/use-program-recommendations/types';
import { PackProgramWithContext } from '@rhg/utils/pack-program/types';
import { isRecommended, sortRecommendations } from '@rhg/utils/recommendations';

interface GetRecommendationPackClassProps {
    shownProgramRecommendations?: RbcCircleRec[];
}
interface GetProgramRecommendationsProps {
    shownProgramRecommendations?: RbcCircleRec[];
    contentPackWorkflowsNotComingSoon?: RelatedIdWorkflowMap;
    packs?: MediaPack[];
}

const getRecommendationPackClassMap = ({
    shownProgramRecommendations,
}: GetRecommendationPackClassProps) => {
    const recommendationPackClassMap = shownProgramRecommendations?.reduce<
        Record<string, RbcCircleRec>
    >((existingMetadata, recommendation) => {
        const recommendationPackClassId =
            recommendation.additionalData.packClassId;
        const existingRecommendation = recommendationPackClassId
            ? existingMetadata[recommendationPackClassId]
            : undefined;
        const isExistingRecommendationRecommended = isRecommended(
            existingRecommendation
        );
        return {
            ...existingMetadata,
            ...(recommendationPackClassId
                ? {
                      [recommendationPackClassId]:
                          isExistingRecommendationRecommended
                              ? existingRecommendation!
                              : recommendation,
                  }
                : {}),
        };
    }, {});

    return recommendationPackClassMap;
};

export function getProgramRecommendations({
    packs,
    shownProgramRecommendations,
    contentPackWorkflowsNotComingSoon,
}: GetProgramRecommendationsProps) {
    const packClassRecommendations = getRecommendationPackClassMap({
        shownProgramRecommendations,
    });
    return (
        packs?.reduce<PackProgramWithContext[]>(
            (programs, pack) => [
                ...programs,
                ...(
                    (pack?.items?.filter(
                        (item) => item.type === 'program'
                    ) as MediaPackProgram[]) ?? []
                ).map<PackProgramWithContext>((packProgram) => ({
                    packClass: pack?.packClass ?? '',
                    workflow: contentPackWorkflowsNotComingSoon
                        ? contentPackWorkflowsNotComingSoon[
                              pack?.packClass ?? ('' as string)
                          ]
                        : undefined,
                    packProgram,
                    recommendation:
                        pack?.packClass && packClassRecommendations
                            ? packClassRecommendations[pack?.packClass]
                            : undefined,
                })),
            ],
            []
        ) ?? []
    );
}

export function sortPackItemsByRecommendation(
    formattedPackItems?: PackProgramWithContext[]
) {
    return formattedPackItems?.sort((a, b) =>
        sortRecommendations(a.recommendation, b.recommendation)
    );
}

export function collectProgramIdsFromPack(pack?: MediaPack): string[] {
    if (!pack?.items) {
        return [];
    }

    const reduced = pack.items
        .filter((item) => !!item)
        .reduce((idsSet, item) => {
            // Only pull programIds from programs and guides. Nested packs are ignored
            if (item.type === 'program' || item.type === 'guide') {
                idsSet.add(item.programId);
            }
            return idsSet;
        }, new Set<string>());
    return Array.from(reduced);
}

export function collectPackClassesFromPack(pack?: MediaPack): string[] {
    if (!pack?.items) {
        return [];
    }
    const reduced = pack.items.reduce((idsSet, item) => {
        // Only pull programIds from programs and guides. Nested packs are ignored
        if (item.type === 'pack') {
            idsSet.add(item.packClass);
        }
        return idsSet;
    }, new Set<string>());
    return Array.from(reduced);
}
